import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Box,
    Typography,
    Chip,
    Button,
    CircularProgress,
    IconButton,
    Tooltip,
    Divider,
    Collapse,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { executeAppStoreStep, deleteAppStoreScreenshot } from '../../redux/actions/courseStoreProductActions';
import api from '../../utils/api/apiClient';

const NAV = '#131F2A';

const STEPS = [
    { key: 'CREATE_IAP',        label: 'Crear IAP' },
    { key: 'SET_TERRITORIES',   label: 'Configurar territorios' },
    { key: 'ADD_LOCALIZATION',  label: 'Agregar localización' },
    { key: 'SET_PRICING',       label: 'Configurar precio' },
    { key: 'UPLOAD_SCREENSHOT', label: 'Subir screenshot de revisión' },
    { key: 'SUBMIT_REVIEW',     label: 'Enviar a revisión' },
];

const STATUS_CONFIG = {
    SUCCESS: { bg: '#e6f4ea', color: '#1e7e34', label: 'Exitoso' },
    FAILED:  { bg: '#fdecea', color: '#c0392b', label: 'Fallido' },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatErrorMessage(raw) {
    if (!raw) return null;
    try {
        const parsed = JSON.parse(raw);
        return JSON.stringify(parsed, null, 2);
    } catch {
        return raw;
    }
}

function getErrorSummary(raw) {
    if (!raw) return null;
    try {
        const parsed = JSON.parse(raw);
        const firstError = parsed?.errors?.[0];
        if (firstError?.detail) return firstError.detail;
        if (firstError?.title) return firstError.title;
    } catch { /* not JSON */ }
    // Truncate plain text to 80 chars
    return raw.length > 80 ? `${raw.slice(0, 80)}…` : raw;
}

// ─── Error Viewer Dialog ──────────────────────────────────────────────────────

const ErrorViewerDialog = ({ open, onClose, stepLabel, errorMessage }) => {
    const [copied, setCopied] = useState(false);
    const formatted = formatErrorMessage(errorMessage);

    const handleCopy = () => {
        navigator.clipboard.writeText(formatted || errorMessage || '');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ErrorOutlineIcon sx={{ color: '#c0392b', fontSize: 18 }} />
                    <Typography fontWeight={700} fontSize={14} color={NAV}>
                        Error — {stepLabel}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Tooltip title={copied ? '¡Copiado!' : 'Copiar'}>
                        <IconButton size="small" onClick={handleCopy}>
                            <ContentCopyIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <IconButton size="small" onClick={onClose}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent sx={{ pt: 0 }}>
                <Box
                    component="pre"
                    sx={{
                        m: 0,
                        p: 2,
                        backgroundColor: '#1e1e1e',
                        borderRadius: '8px',
                        overflowX: 'auto',
                        fontSize: 12,
                        lineHeight: 1.6,
                        fontFamily: '"Fira Code", "Courier New", monospace',
                        color: '#d4d4d4',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        maxHeight: '60vh',
                        overflowY: 'auto',
                    }}
                >
                    <JsonHighlight text={formatted || errorMessage || ''} />
                </Box>
            </DialogContent>
        </Dialog>
    );
};

// ─── Simple JSON syntax highlighter ──────────────────────────────────────────

const TOKEN_COLORS = {
    key:     '#9cdcfe',
    string:  '#ce9178',
    number:  '#b5cea8',
    boolean: '#569cd6',
    null:    '#569cd6',
    brace:   '#ffd700',
};

function JsonHighlight({ text }) {
    // If it's not JSON-like, render as plain text
    const trimmed = text.trim();
    if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
        return <span style={{ color: '#d4d4d4' }}>{text}</span>;
    }

    // Tokenise with a simple regex
    const regex = /("(?:[^"\\]|\\.)*"\s*:)|("(?:[^"\\]|\\.)*")|(true|false|null)|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)|([{}[\],])/g;
    const parts = [];
    let last = 0;
    let match;

    while ((match = regex.exec(trimmed)) !== null) {
        if (match.index > last) {
            parts.push(<span key={last}>{trimmed.slice(last, match.index)}</span>);
        }
        const [full, keyToken, strToken, boolNull, num, punct] = match;
        if (keyToken) {
            const colonIdx = keyToken.lastIndexOf(':');
            parts.push(
                <span key={match.index} style={{ color: TOKEN_COLORS.key }}>{keyToken.slice(0, colonIdx)}</span>,
                <span key={`${match.index}c`} style={{ color: '#d4d4d4' }}>:</span>,
            );
        } else if (strToken) {
            parts.push(<span key={match.index} style={{ color: TOKEN_COLORS.string }}>{strToken}</span>);
        } else if (boolNull) {
            parts.push(<span key={match.index} style={{ color: TOKEN_COLORS.boolean }}>{boolNull}</span>);
        } else if (num) {
            parts.push(<span key={match.index} style={{ color: TOKEN_COLORS.number }}>{num}</span>);
        } else if (punct) {
            parts.push(<span key={match.index} style={{ color: TOKEN_COLORS.brace }}>{punct}</span>);
        }
        last = match.index + full.length;
    }

    if (last < trimmed.length) {
        parts.push(<span key={last}>{trimmed.slice(last)}</span>);
    }

    return <>{parts}</>;
}

// ─── Screenshot specs ────────────────────────────────────────────────────────

const SCREENSHOT_SPECS = [
    {
        device: 'iPhone',
        sizes: [
            { label: '6.9"  (17 Pro Max, 16 Pro Max)',  portrait: '1320 × 2868' },
            { label: '6.5"  (14 Plus, 13 Pro Max)',      portrait: '1284 × 2778' },
            { label: '6.3"  (17 Pro, 16 Pro)',           portrait: '1179 × 2556' },
            { label: '6.1"  (14, 13, 12)',               portrait: '1170 × 2532' },
            { label: '5.5"  (8 Plus, 7 Plus)',           portrait: '1242 × 2208' },
        ],
    },
    {
        device: 'iPad',
        sizes: [
            { label: '13"   (iPad Pro M4/M5)',           portrait: '2064 × 2752' },
            { label: '12.9" (iPad Pro 2nd gen)',         portrait: '2048 × 2732' },
            { label: '11"   (iPad Pro, Air)',            portrait: '1488 × 2266' },
        ],
    },
];

const ScreenshotSpecsPanel = () => {
    const [open, setOpen] = useState(false);
    return (
        <Box sx={{ mt: 1 }}>
            <Box
                onClick={() => setOpen((v) => !v)}
                sx={{
                    display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer',
                    color: '#555', userSelect: 'none',
                }}
            >
                <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                <Typography variant="caption" sx={{ fontWeight: 500 }}>
                    Dimensiones permitidas
                </Typography>
                {open ? <ExpandLessIcon sx={{ fontSize: 14 }} /> : <ExpandMoreIcon sx={{ fontSize: 14 }} />}
            </Box>
            <Collapse in={open}>
                <Box sx={{ mt: 0.75, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                    <Typography variant="caption" color="text.secondary">
                        Formatos: <strong>JPG, PNG</strong>
                    </Typography>
                    {SCREENSHOT_SPECS.map((group) => (
                        <Box key={group.device}>
                            <Typography
                                variant="caption"
                                sx={{ fontWeight: 700, color: NAV, display: 'block', mb: 0.5 }}
                            >
                                {group.device}
                            </Typography>
                            <Box
                                component="table"
                                sx={{
                                    borderCollapse: 'collapse',
                                    width: '100%',
                                    fontSize: 11,
                                    '& td': { py: '2px', pr: 1.5, color: '#444', verticalAlign: 'top' },
                                    '& td:last-child': { fontFamily: 'monospace', color: NAV, whiteSpace: 'nowrap' },
                                }}
                            >
                                <tbody>
                                    {group.sizes.map((s) => (
                                        <tr key={s.label}>
                                            <td>{s.label}</td>
                                            <td>{s.portrait} px</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Collapse>
        </Box>
    );
};

// ─── Main Modal ───────────────────────────────────────────────────────────────

const IAP_STATE_COLORS = {
    APPROVED:                { bg: '#e6f4ea', color: '#1e7e34' },
    IN_REVIEW:               { bg: '#e3f2fd', color: '#1565c0' },
    WAITING_FOR_REVIEW:      { bg: '#e3f2fd', color: '#1565c0' },
    READY_TO_SUBMIT:         { bg: '#fff8e1', color: '#b8860b' },
    PREPARE_FOR_SUBMISSION:  { bg: '#fff8e1', color: '#b8860b' },
    MISSING_METADATA:        { bg: '#fdecea', color: '#c0392b' },
    REJECTED:                { bg: '#fdecea', color: '#c0392b' },
    DEVELOPER_ACTION_NEEDED: { bg: '#fdecea', color: '#c0392b' },
    WAITING_FOR_UPLOAD:      { bg: '#f0f0f0', color: '#555' },
    PROCESSING_CONTENT:      { bg: '#f0f0f0', color: '#555' },
};

const AppStoreStepsModal = ({ open, onClose, courseId }) => {
    const dispatch = useDispatch();
    const { isExecutingStep, records } = useSelector((state) => state.courseStoreProduct);
    const [screenshotFile, setScreenshotFile] = useState(null);
    const [isDeletingScreenshot, setIsDeletingScreenshot] = useState(false);
    const [errorViewer, setErrorViewer] = useState(null); // { stepLabel, errorMessage }
    const [iapState, setIapState] = useState(null);       // { state, name, productId }
    const [fetchingState, setFetchingState] = useState(false);
    const [stateError, setStateError] = useState(null);

    const handleFetchState = async () => {
        setFetchingState(true);
        setStateError(null);
        try {
            const { data } = await api.courseStoreProduct.getAppStoreStatus(courseId);
            setIapState(data);
        } catch (e) {
            setStateError(e?.response?.data?.message || 'Error al consultar estado');
        } finally {
            setFetchingState(false);
        }
    };

    const appStoreRecord = records.find((r) => r.store === 'APP_STORE' && r.course_id === Number(courseId)) || null;
    const steps = appStoreRecord?.app_store_steps || [];
    const storeRef = appStoreRecord?.store_ref || null;

    const stepMap = {};
    steps.forEach((s) => { stepMap[s.step] = s; });

    const hasStoreRef = Boolean(storeRef);

    const isStepDisabled = (stepKey) => {
        if (isExecutingStep) return true;
        if (stepKey === 'CREATE_IAP') return stepMap['CREATE_IAP']?.status === 'SUCCESS';
        if (!hasStoreRef) return true;
        if (stepKey === 'UPLOAD_SCREENSHOT' && !screenshotFile) return true;
        return false;
    };

    const getButtonLabel = (stepKey) => {
        const record = stepMap[stepKey];
        if (!record) return 'Ejecutar';
        return 'Reintentar';
    };

    const handleDeleteScreenshot = async () => {
        setIsDeletingScreenshot(true);
        await dispatch(deleteAppStoreScreenshot(courseId));
        setIsDeletingScreenshot(false);
    };

    const handleExecute = (stepKey) => {
        dispatch(executeAppStoreStep(
            courseId,
            stepKey,
            stepKey === 'UPLOAD_SCREENSHOT' ? screenshotFile : undefined,
        ));
        if (stepKey === 'UPLOAD_SCREENSHOT') setScreenshotFile(null);
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                    <Typography fontWeight={700} fontSize={16} color={NAV}>
                        Registro en App Store
                    </Typography>
                    <IconButton size="small" onClick={onClose}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ pt: 1 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {STEPS.map((step, index) => {
                            const record = stepMap[step.key];
                            const statusConf = record ? STATUS_CONFIG[record.status] : null;
                            const isLoading = isExecutingStep === step.key;
                            const disabled = isStepDisabled(step.key);
                            const hasFailed = record?.status === 'FAILED' && record.error_message;

                            return (
                                <Box
                                    key={step.key}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 0.5,
                                        p: 1.5,
                                        border: `1px solid ${hasFailed ? 'rgba(192,57,43,0.25)' : 'rgba(19,31,42,0.12)'}`,
                                        borderRadius: '8px',
                                        backgroundColor: record?.status === 'SUCCESS' ? '#f8fdf9' : '#fff',
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                                        {/* Left: number + label + status */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    width: 22, height: 22,
                                                    borderRadius: '50%',
                                                    backgroundColor: hasFailed ? '#c0392b' : NAV,
                                                    color: '#fff',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    fontWeight: 700, fontSize: 11, flexShrink: 0,
                                                }}
                                            >
                                                {index + 1}
                                            </Typography>
                                            <Typography variant="body2" fontWeight={500} color={NAV} noWrap>
                                                {step.label}
                                            </Typography>
                                            {statusConf && (
                                                <Chip
                                                    label={statusConf.label}
                                                    size="small"
                                                    sx={{ backgroundColor: statusConf.bg, color: statusConf.color, fontWeight: 600, fontSize: 10, height: 20, flexShrink: 0 }}
                                                />
                                            )}
                                            {!record && (
                                                <Chip
                                                    label="Pendiente"
                                                    size="small"
                                                    sx={{ backgroundColor: '#f0f0f0', color: '#666', fontWeight: 600, fontSize: 10, height: 20, flexShrink: 0 }}
                                                />
                                            )}
                                        </Box>

                                        {/* Right: error icon + execute button */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                                            {hasFailed && (
                                                <Tooltip title="Ver detalle del error">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setErrorViewer({ stepLabel: step.label, errorMessage: record.error_message })}
                                                        sx={{ color: '#c0392b' }}
                                                    >
                                                        <ErrorOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            <Button
                                                variant="contained"
                                                size="small"
                                                disabled={disabled}
                                                onClick={() => handleExecute(step.key)}
                                                sx={{
                                                    backgroundColor: NAV,
                                                    boxShadow: 'none',
                                                    minWidth: 100,
                                                    fontSize: 12,
                                                    '&:hover': { backgroundColor: '#1e2f3e', boxShadow: 'none' },
                                                    '&.Mui-disabled': { backgroundColor: 'rgba(19,31,42,0.25)', color: '#fff' },
                                                }}
                                            >
                                                {isLoading
                                                    ? <CircularProgress size={14} sx={{ color: '#fff' }} />
                                                    : getButtonLabel(step.key)
                                                }
                                            </Button>
                                        </Box>
                                    </Box>

                                    {/* Error summary (one line) */}
                                    {hasFailed && (
                                        <Box
                                            sx={{
                                                pl: '30px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                                cursor: 'pointer',
                                            }}
                                            onClick={() => setErrorViewer({ stepLabel: step.label, errorMessage: record.error_message })}
                                        >
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: '#c0392b',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    flex: 1,
                                                    '&:hover': { textDecoration: 'underline' },
                                                }}
                                            >
                                                {getErrorSummary(record.error_message)}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: '#c0392b', flexShrink: 0, opacity: 0.7 }}>
                                                Ver más →
                                            </Typography>
                                        </Box>
                                    )}

                                    {/* Screenshot file picker */}
                                    {step.key === 'UPLOAD_SCREENSHOT' && (
                                        <Box sx={{ pl: '30px', mt: 0.5 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Button
                                                    component="label"
                                                    variant="outlined"
                                                    size="small"
                                                    sx={{
                                                        fontSize: 11,
                                                        borderColor: 'rgba(19,31,42,0.3)',
                                                        color: NAV,
                                                        '&:hover': { borderColor: NAV },
                                                    }}
                                                >
                                                    {screenshotFile ? screenshotFile.name : 'Elegir imagen'}
                                                    <input
                                                        type="file"
                                                        accept=".jpg,.jpeg,.png"
                                                        hidden
                                                        onChange={(e) => setScreenshotFile(e.target.files[0] || null)}
                                                    />
                                                </Button>
                                                {record?.status === 'SUCCESS' && (
                                                    <Tooltip title="Eliminar screenshot de Apple">
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                disabled={isDeletingScreenshot || Boolean(isExecutingStep)}
                                                                onClick={handleDeleteScreenshot}
                                                                sx={{ color: '#c0392b' }}
                                                            >
                                                                {isDeletingScreenshot
                                                                    ? <CircularProgress size={14} sx={{ color: '#c0392b' }} />
                                                                    : <DeleteOutlineIcon fontSize="small" />
                                                                }
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                            <ScreenshotSpecsPanel />
                                        </Box>
                                    )}

                                    {/* Executed at */}
                                    {record?.executed_at && (
                                        <Typography variant="caption" color="text.secondary" sx={{ pl: '30px' }}>
                                            {new Date(record.executed_at).toLocaleDateString('es', {
                                                day: '2-digit', month: 'short', year: 'numeric',
                                                hour: '2-digit', minute: '2-digit',
                                            })}
                                        </Typography>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>

                    {/* ── Apple review state ── */}
                    {storeRef && (
                        <>
                            <Divider sx={{ borderColor: 'rgba(19,31,42,0.1)', mt: 0.5 }} />
                            <Box
                                sx={{
                                    p: 1.5,
                                    border: '1px solid rgba(19,31,42,0.12)',
                                    borderRadius: '8px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1,
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" fontWeight={600} color={NAV}>
                                        Estado en Apple
                                    </Typography>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={fetchingState ? <CircularProgress size={12} /> : <RefreshIcon sx={{ fontSize: 14 }} />}
                                        disabled={fetchingState}
                                        onClick={handleFetchState}
                                        sx={{
                                            fontSize: 11,
                                            borderColor: 'rgba(19,31,42,0.3)',
                                            color: NAV,
                                            '&:hover': { borderColor: NAV },
                                        }}
                                    >
                                        Consultar
                                    </Button>
                                </Box>

                                {iapState && (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                        <Chip
                                            label={iapState.state}
                                            size="small"
                                            sx={{
                                                backgroundColor: (IAP_STATE_COLORS[iapState.state] || { bg: '#f0f0f0' }).bg,
                                                color: (IAP_STATE_COLORS[iapState.state] || { color: '#555' }).color,
                                                fontWeight: 600,
                                                fontSize: 10,
                                                height: 20,
                                                alignSelf: 'flex-start',
                                            }}
                                        />
                                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                            {iapState.productId}
                                        </Typography>
                                    </Box>
                                )}

                                {stateError && (
                                    <Typography variant="caption" sx={{ color: '#c0392b' }}>
                                        {stateError}
                                    </Typography>
                                )}

                                {!iapState && !stateError && !fetchingState && (
                                    <Typography variant="caption" color="text.secondary">
                                        Presiona "Consultar" para ver el estado actual en Apple.
                                    </Typography>
                                )}
                            </Box>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {errorViewer && (
                <ErrorViewerDialog
                    open={Boolean(errorViewer)}
                    onClose={() => setErrorViewer(null)}
                    stepLabel={errorViewer.stepLabel}
                    errorMessage={errorViewer.errorMessage}
                />
            )}
        </>
    );
};

export default AppStoreStepsModal;
